import { useEffect, useMemo } from 'react';
import { addYears } from 'date-fns';
import {
  Alert,
  Center,
  Form,
  Grid,
  useForm,
  SIZE,
  Stack,
  Text,
  Spinner,
} from '@quinyx/ui';
import * as z from 'zod';

import { countries, countryNames, languages } from '@/config';
import { DATE_FORMAT } from '@/constants/time';
import { format, formatDate, newDate } from '@/utils/time';
import {
  ICustomer,
  ICustomerBasicInfo,
  ICustomerError,
} from '../../api/interfaces/customer';
import { useTranslate } from '../../hooks/translate';

type Props = {
  initialValues?: ICustomer;
  isMarkedForDeletion?: boolean;
  onSubmit: (customerForm: ICustomerBasicInfo) => void;
  isLoading: boolean;
  error: ICustomerError | null;
};

const schema = z.object({
  name: z.string().min(1),
  companyAlias: z.preprocess(
    (val) => (val === '' || val == null ? undefined : val),
    z.string().optional(),
  ),
  locale: z.string().min(1),
  country: z.enum(countryNames),
  timeZone: z.string().min(1),
  salesForceId: z.string().min(1),
  dateCreated: z.date(),
  validUpto: z.date(),
  useDistricts: z.boolean().default(false),
  localShiftTypes: z.boolean().default(false),
  shareWithinDistrict: z.boolean().default(false),
  active: z.boolean().default(false),
});

const DEFAULT_DATE_CREATED = new Date().toISOString().split('T')[0];
const DEFAULT_VALID_UPTO = '2050-01-15';

const CustomerForm = ({
  initialValues,
  onSubmit,
  isLoading,
  error,
  isMarkedForDeletion,
}: Props) => {
  const { t, selectedLanguage } = useTranslate();
  const formMethods = useForm({
    defaultValues: {
      ...initialValues,
      dateCreated: newDate(initialValues?.dateCreated ?? DEFAULT_DATE_CREATED),
      validUpto: newDate(initialValues?.validUpto ?? DEFAULT_VALID_UPTO),
      useDistricts: initialValues?.useDistricts ?? false,
      localShiftTypes: initialValues?.localShiftTypes ?? false,
      shareWithinDistrict: initialValues?.shareWithinDistrict ?? false,
      active: initialValues?.active ?? false,
    },
    schema,
  });

  useEffect(() => {
    formMethods.clearErrors();

    if (!error) {
      return;
    }

    if (error?.response?.data?.additionalErrorParameters?.exceptionMessage) {
      formMethods.setError('companyAlias', {
        type: 'server',
        message: t('error.customer.alias.name.already_exists'),
      });
    }
  }, [error, formMethods, t]);

  const timeZones = (Intl as any)?.supportedValuesOf?.('timeZone') || [];

  const dayPickerMonthRange = useMemo(
    () => ({
      startMonth: addYears(new Date(), -50),
      endMonth: addYears(new Date(), 50),
    }),
    [],
  );

  const handleSubmit = (values: z.infer<typeof schema>) => {
    onSubmit({
      ...values,
      dateCreated: format(values.dateCreated, DATE_FORMAT),
      validUpto: format(values.validUpto, DATE_FORMAT),
    });
  };

  if (isLoading) {
    return (
      <Center>
        <Spinner size={SIZE.L} />
      </Center>
    );
  }

  return (
    <Form methods={formMethods} onSubmit={handleSubmit}>
      {isMarkedForDeletion && (
        <Alert type="info">
          <Text variant="smallRegular">
            {t('customer.markedForDeletionAlert')}
          </Text>
        </Alert>
      )}
      <Grid columns={[1, 1, 1, 2]} gap={SIZE.M}>
        <Form.TextInput
          data-test-id="customer-name-input"
          name="name"
          label={t('customer.customerName')}
          required
          disabled={isMarkedForDeletion}
        />
        <Form.TextInput
          data-test-id="customer-company-alias-input"
          name="companyAlias"
          label={t('customer.companyAlias')}
          info={t('customer.companyAliasInfo')}
          disabled={isMarkedForDeletion}
        />
        <Form.Combobox
          data-test-id="customer-language-select"
          name="locale"
          label={t('customer.language')}
          allowFiltering
          options={languages.map((language) => ({
            label: language.name,
            value: language.locale,
          }))}
          required
          disabled={isMarkedForDeletion}
        />
        <Form.Combobox
          data-test-id="customer-country-select"
          name="country"
          label={t('customer.country')}
          allowFiltering
          options={Object.values(countries).map((country) => ({
            label: country,
            value: country,
          }))}
          required
          disabled={isMarkedForDeletion}
        />
        <Form.Combobox
          data-test-id="customer-time-zone-select"
          name="timeZone"
          label={t('customer.timeZone')}
          allowFiltering
          options={timeZones.map((timeZone: string) => ({
            label: timeZone,
            value: timeZone,
          }))}
          required
          disabled={isMarkedForDeletion}
        />
        <Form.TextInput
          data-test-id="customer-salesforce-id-input"
          name="salesForceId"
          label={t('customer.salesforceId')}
          info={t('customer.salesforceIdInfo')}
          required
          disabled={isMarkedForDeletion}
        />
        <Form.DayInput
          data-test-id="customer-creation-date-picker"
          name="dateCreated"
          mode="single"
          label={t('customer.creationDate')}
          startMonth={dayPickerMonthRange.startMonth}
          endMonth={dayPickerMonthRange.endMonth}
          dateFormatter={(date) =>
            date ? formatDate(date, selectedLanguage) : ''
          }
        />
        <Form.DayInput
          data-test-id="customer-valid-upto-date-picker"
          name="validUpto"
          mode="single"
          label={t('customer.validUpTo')}
          startMonth={dayPickerMonthRange.startMonth}
          endMonth={dayPickerMonthRange.endMonth}
          dateFormatter={(date) =>
            date ? formatDate(date, selectedLanguage) : ''
          }
        />
      </Grid>

      <Stack spacing={SIZE.S}>
        <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
          {t('customer.baseConfiguration')}
        </Text>

        <Stack spacing={SIZE.XS}>
          <Form.Checkbox
            data-test-id="customer-use-districts-checkbox"
            name="useDistricts"
            label={t('customer.useDistricts')}
            info={t('customer.useDistrictsInfo')}
            disabled={isMarkedForDeletion}
          />
          <Form.Checkbox
            data-test-id="customer-local-shift-types-checkbox"
            name="localShiftTypes"
            label={t('customer.localShiftTypes')}
            info={t('customer.localShiftTypesInfo')}
            disabled={isMarkedForDeletion}
          />
          <Form.Checkbox
            data-test-id="customer-share-staff-within-district-checkbox"
            name="shareWithinDistrict"
            label={t('customer.shareStaffWithinDistrict')}
            info={t('customer.shareStaffWithinDistrictInfo')}
            disabled={isMarkedForDeletion}
          />
          <Form.Checkbox
            data-test-id="customer-is-active-checkbox"
            name="active"
            label={t('customer.isActive')}
            disabled={isMarkedForDeletion}
          />
        </Stack>

        <Stack alignItems="flex-end">
          <Form.SubmitButton
            data-test-id="update-customer-button"
            text={
              initialValues
                ? t('customer.updateCustomer')
                : t('customer.createCustomer')
            }
            isLoading={isLoading}
            disabled={isMarkedForDeletion}
          />
        </Stack>
      </Stack>
    </Form>
  );
};

export default CustomerForm;
